import React from 'react';
import classnames from 'classnames';

import { CheckmarkIcon, WarningIcon, ErrorIcon, CloseIcon } from '../icons/icons';
import { IconButton } from '../actions';
import { Icon } from '../icon';
import Colors from '../colors';

import styles from './banner.css';
import { BannerType } from './types';

interface BannerProps {
  /** Message to be shown on the banner */
  children?: React.ReactNode;
  /** Provides visual elements that determine if the banner is an error, a warning or a success message */
  type?: BannerType;
  /** Callback function for when the close icon is clicked. When empty, close icon will not be shown */
  onClose?: () => void;
  /** Adds additional classes to the component. */
  className?: string;
  /** Provides a shadow as visual elements */
  dropShadow?: boolean;
  /** Provides a data-testid */
  'data-testid'?: string;
}

type ColorMap = { [key in BannerType]: Colors };

const colorMap: ColorMap = {
  error: Colors.Error,
  success: Colors.Success,
  warning: Colors.Warning
};

/**
 * Banner Component
 *
 * @param {BannerProps} props
 *
 * @returns JSX.ELEMENT
 */
export const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
  (
    {
      children,
      className,
      onClose,
      'data-testid': dataTestId = 'banner',
      type = 'warning',
      dropShadow = false
    },
    ref
  ) => {
    const bannerClasses = classnames(styles.banner, className, {
      [styles[type]]: true,
      [styles.dropShadow]: dropShadow
    });

    return (
      <div className={bannerClasses} data-testid={dataTestId} ref={ref}>
        <Icon color={colorMap[type]} className={styles.icon}>
          {type === 'success' ? (
            <CheckmarkIcon />
          ) : type === 'error' ? (
            <ErrorIcon />
          ) : (
            <WarningIcon />
          )}
        </Icon>
        <div className={styles.text} data-testid="banner-text">
          {children}
        </div>
        {onClose && (
          <IconButton color={colorMap[type]} className={styles.closeIcon} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        )}
      </div>
    );
  }
);
